/**
 * Root Application Component — route-based layout
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import { useAuth } from './contexts/AuthContext';
import { RequireRole } from './components/auth/RequireRole';
import { RoleRedirect } from './components/auth/RoleRedirect';
import { ForcePasswordChange } from './components/ForcePasswordChange';
import { LoginPage } from './pages/LoginPage';
import { SignUpPage } from './pages/SignUpPage';
import { PendingApprovalPage } from './pages/PendingApprovalPage';

// Admin pages
import { AdminLayout } from './pages/admin/AdminLayout';
import { DashboardPage } from './pages/admin/DashboardPage';
import { ProjectsPage } from './pages/admin/ProjectsPage';
import { ProjectDetailPage } from './pages/admin/ProjectDetailPage';
import { ClientsPage } from './pages/admin/ClientsPage';
import { ClientDetailPage } from './pages/admin/ClientDetailPage';
import { UsersPage } from './pages/admin/UsersPage';
import { AdminProjectViewer } from './pages/admin/AdminProjectViewer';
import { MapEditorPage } from './features/map-editor/MapEditorPage';

// Portal pages
import { PortalLayout } from './pages/portal/PortalLayout';
import { PortalProjectsPage } from './pages/portal/PortalProjectsPage';
import { PortalProjectViewer } from './pages/portal/PortalProjectViewer';
import { PortalSettingsPage } from './pages/portal/PortalSettingsPage';

// Share link page
import { ShareLinkViewer } from './pages/ShareLinkViewer';

export function App() {
  const { user } = useAuth();

  if (user?.mustChangePassword) {
    return <ForcePasswordChange />;
  }

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/pending" element={<PendingApprovalPage />} />
      <Route path="/sso-callback" element={<AuthenticateWithRedirectCallback />} />
      <Route path="/share/:token" element={<ShareLinkViewer />} />

      {/* Admin — requires admin role */}
      <Route element={<RequireRole role="admin" />}>
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:id" element={<ProjectDetailPage />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/:id" element={<ClientDetailPage />} />
          <Route path="users" element={<UsersPage />} />
        </Route>
        <Route path="/admin/projects/:id/view" element={<AdminProjectViewer />} />
        <Route path="/admin/projects/:id/map-editor" element={<MapEditorPage />} />
      </Route>

      {/* Client portal — requires client role */}
      <Route element={<RequireRole role="client" />}>
        <Route path="/portal" element={<PortalLayout />}>
          <Route index element={<PortalProjectsPage />} />
          <Route path="projects" element={<PortalProjectsPage />} />
          <Route path="projects/:id" element={<PortalProjectViewer />} />
          <Route path="members" element={<Navigate to="/portal/settings" replace />} />
          <Route path="settings" element={<PortalSettingsPage />} />
        </Route>
      </Route>

      {/* Root — role-based redirect */}
      <Route path="*" element={<RoleRedirect />} />
    </Routes>
  );
}
